import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import type { GuildMemberView, GuildRosterSlotView, GuildBattleInfoView, GuildBattleLiveView, RosterReadySlot } from "../api/guild";
import { guildApi, type GuildSkillTowerResponse } from "../api/guild";
import { useGameStore } from "../store/useGameStore";
import {
  formatBattleCountdown,
  formatSecondsRemaining,
  palaceLabel,
  useGuildStore,
} from "../store/useGuildStore";
import { PlatinumSeatGrid } from "../components/guild/PlatinumSeatGrid";
import { GuildTournamentBracket, GuildBattleBracketIntro } from "../components/guild/GuildTournamentBracket";
import { GuildBattlePhaseStepper } from "../components/guild/GuildBattlePhaseStepper";
import { GuildBattleReplayOverlay } from "../components/guild/GuildBattleReplayOverlay";
import { PalaceDetailModal } from "../components/guild/PalaceDetailModal";
import { GuildAltarPanel } from "../components/guild/GuildAltarPanel";
import { formatGuildBattleScheduleLocal } from "../utils/localSchedule";

const TABS = [
  { id: "home" as const, label: "Home" },
  { id: "tower" as const, label: "Skill Tower" },
  { id: "altar" as const, label: "Altar" },
  { id: "battle" as const, label: "Battle" },
  { id: "shop" as const, label: "Shop" },
  { id: "members" as const, label: "Members" },
];

export function GuildScreen() {
  const { setScreen, hero, balance, user } = useGameStore();
  const store = useGuildStore();
  const {
    overview,
    searchResults,
    members,
    applications,
    shopItems,
    myContribution,
    npcFillers,
    treasuryGold,
    battleLogs,
    battleLive,
    dismissedReplayMatchIds,
    platinumVotes,
    loading,
    actionLoading,
    error,
    message,
    refresh,
    search,
    createGuild,
    apply,
    leave,
    loadMembers,
    loadApplications,
    acceptApplication,
    rejectApplication,
    donateGold,
    setRoster,
    loadShop,
    buyItem,
    loadNpcFillers,
    promoteMember,
    loadBattleLogs,
    loadBattleLive,
    enterBattle,
    claimPlatinumSeat,
    ackBattleReplay,
    completePalaceWatch,
    dismissReplayMatch,
    loadPlatinumVotes,
    createPlatinumVote,
    castPlatinumVote,
    clearMessage,
  } = store;

  const [tab, setTab] = useState<(typeof TABS)[number]["id"]>("home");
  const [mode, setMode] = useState<"main" | "create" | "find">("main");
  const [guildName, setGuildName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [donateAmount, setDonateAmount] = useState("10000");
  const [voteTitle, setVoteTitle] = useState("");
  const [voteDesc, setVoteDesc] = useState("");
  const [palaceDetailKey, setPalaceDetailKey] = useState<string | null>(null);
  const [spectatorReplayDismissed, setSpectatorReplayDismissed] = useState(false);
  const [bracketIntroDismissed, setBracketIntroDismissed] = useState(false);
  const [bracketIntroPhaseKey, setBracketIntroPhaseKey] = useState<string | null>(null);
  const [showPostBattleBracket, setShowPostBattleBracket] = useState(false);
  const [skillTower, setSkillTower] = useState<GuildSkillTowerResponse | null>(null);

  const handleReplayAck = useCallback(
    (matchId: string, opts?: { warFinal?: boolean; showBracket?: boolean }) => {
      if (opts?.showBracket) {
        setShowPostBattleBracket(true);
        setTab("battle");
      }
      void ackBattleReplay(matchId);
    },
    [ackBattleReplay],
  );

  const handleWatchComplete = useCallback(() => {
    void completePalaceWatch();
  }, [completePalaceWatch]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (tab === "members" && overview?.in_guild) void loadMembers();
    if (tab === "shop" && overview?.in_guild) void loadShop();
    if (tab === "tower" && overview?.in_guild) {
      void guildApi.skillTower().then(setSkillTower).catch(() => setSkillTower(null));
    }
    if (tab === "battle" && overview?.in_guild) {
      void loadMembers();
      void loadNpcFillers();
      void loadBattleLogs();
      void loadBattleLive();
    }
    if (overview?.in_guild) void loadPlatinumVotes();
    if (overview?.my_guild?.can_manage_applications) void loadApplications();
  }, [
    tab,
    overview?.in_guild,
    overview?.my_guild?.can_manage_applications,
    loadMembers,
    loadShop,
    loadApplications,
    loadNpcFillers,
    loadBattleLogs,
    loadBattleLive,
    loadPlatinumVotes,
  ]);

  useEffect(() => {
    if (mode !== "find") return;
    const timer = setTimeout(() => void search(searchQuery), 250);
    return () => clearTimeout(timer);
  }, [searchQuery, mode, search]);

  const myGuild = overview?.my_guild;
  const battle = overview?.battle;
  const live = battleLive ?? battle?.live ?? null;

  useEffect(() => {
    if (!live?.current_match?.id) return;
    if (live.my_replay_ack_done || dismissedReplayMatchIds.includes(live.current_match.id)) {
      return;
    }
    setSpectatorReplayDismissed(false);
  }, [live?.current_match?.id, live?.my_replay_ack_done, dismissedReplayMatchIds]);

  const bracketPhaseKey = live?.active ? `${live.week_key}-${live.phase}` : null;

  useEffect(() => {
    if (!bracketPhaseKey) return;
    const introEligible =
      live?.status === "awaiting_replay" ||
      (live?.status === "resolving" && !live.current_match);
    if (introEligible && bracketPhaseKey !== bracketIntroPhaseKey) {
      setBracketIntroPhaseKey(bracketPhaseKey);
      setBracketIntroDismissed(false);
      setShowPostBattleBracket(false);
      setTab("battle");
    }
  }, [bracketPhaseKey, live?.status, live?.current_match?.id, bracketIntroPhaseKey]);

  useEffect(() => {
    if (!bracketPhaseKey || bracketIntroDismissed || bracketIntroPhaseKey !== bracketPhaseKey) return;
    if (showPostBattleBracket) return;
    const timer = setTimeout(() => setBracketIntroDismissed(true), 10000);
    return () => clearTimeout(timer);
  }, [bracketPhaseKey, bracketIntroPhaseKey, bracketIntroDismissed, showPostBattleBracket]);

  const hasBracketData =
    live?.battle_bracket != null || (live?.tournament_tree?.rounds?.length ?? 0) > 0;

  const palaceWatch = Boolean(live?.palace_watch);
  const platinumWatch = Boolean(live?.platinum_watch);
  const queueWatch = palaceWatch || platinumWatch;
  const hasWatchQueue = queueWatch && (live?.match_queue?.length ?? 0) > 0;
  const currentMatch = live?.current_match ?? null;
  const liveWatchMode =
    queueWatch || (live?.status === "awaiting_replay" && !live.my_replay_ack_required);

  const showPreBattleIntro =
    !liveWatchMode &&
    !platinumWatch &&
    bracketPhaseKey != null &&
    bracketIntroPhaseKey === bracketPhaseKey &&
    !bracketIntroDismissed &&
    !showPostBattleBracket &&
    (live?.status === "resolving" || (live?.status === "awaiting_replay" && (live.current_match?.round ?? 1) <= 1));

  const showBracketIntro = showPreBattleIntro && (hasBracketData || live?.status === "resolving");

  const replayMatchId = currentMatch?.id;
  const replayAlreadyHandled =
    !liveWatchMode &&
    (!replayMatchId ||
      live?.my_replay_ack_done ||
      dismissedReplayMatchIds.includes(replayMatchId));

  const showReplayOverlay =
    live?.status === "awaiting_replay" &&
    !showBracketIntro &&
    !showPostBattleBracket &&
    (
      (queueWatch && (hasWatchQueue || currentMatch != null)) ||
      (!queueWatch &&
        currentMatch != null &&
        (liveWatchMode || (!replayAlreadyHandled && bracketIntroDismissed)) &&
        !(spectatorReplayDismissed && !live.my_replay_ack_required && !liveWatchMode))
    );

  useEffect(() => {
    if (!overview?.in_guild) return;
    const fastPoll =
      live?.active ||
      live?.in_battle_window ||
      live?.checkin_open ||
      live?.status === "awaiting_replay" ||
      live?.status === "resolving";
    if (!fastPoll) return;
    void loadBattleLive();
    const ms = 2000;
    const timer = setInterval(() => void loadBattleLive(), ms);
    return () => clearInterval(timer);
  }, [
    overview?.in_guild,
    loadBattleLive,
    live?.active,
    live?.status,
    live?.checkin_open,
    live?.in_battle_window,
  ]);

  const prevLiveStatusRef = useRef<string | null>(live?.status ?? null);
  useEffect(() => {
    const prev = prevLiveStatusRef.current;
    const next = live?.status ?? null;
    prevLiveStatusRef.current = next;
    if (prev == null || prev === next) return;
    const leftBattleFlow =
      (prev === "awaiting_replay" || prev === "resolving" || prev === "checkin") &&
      (next === "complete" || next == null || live?.active === false);
    const phaseEnded = prev === "awaiting_replay" && next === "resolving";
    const watchToPlatinum = prev === "awaiting_replay" && next === "checkin" && live?.phase === "platinum";
    if (leftBattleFlow || phaseEnded || watchToPlatinum || next === "complete") {
      void refresh();
    }
  }, [live?.status, live?.active, live?.phase, refresh]);
  const locked = overview && !overview.unlocked;
  const isServerMaster = overview?.platinum_seats.some(
    (s) => s.seat_index === 0 && s.user_id === user?.id,
  );
  const hasPlatinumSeat = overview?.platinum_seats.some((s) => s.user_id === user?.id);

  return (
    <div className="p-4 space-y-4 pb-8">
      {showBracketIntro && (
        <GuildBattleBracketIntro
          bracket={live?.battle_bracket ?? null}
          tree={live?.tournament_tree ?? null}
          phaseLabel={live?.phase_label}
          onDone={() => setBracketIntroDismissed(true)}
        />
      )}
      {showPostBattleBracket && (
        <GuildBattleBracketIntro
          bracket={live?.battle_bracket ?? null}
          tree={live?.tournament_tree ?? null}
          phaseLabel={live?.last_match_summary?.round
            ? `Round ${live.last_match_summary.round} complete`
            : live?.phase_label}
          title={live?.last_match_summary ? "⚔️ Round complete" : "⚔️ Battle complete"}
          onDone={() => setShowPostBattleBracket(false)}
        />
      )}
      {showReplayOverlay && (currentMatch || hasWatchQueue) && (
        <GuildBattleReplayOverlay
          match={currentMatch ?? live!.match_queue![0]}
          matchQueue={live?.match_queue}
          queueWatch={queueWatch}
          palaceWatch={palaceWatch}
          heroClass={hero?.hero_class}
          heroName={hero?.name ?? hero?.hero_class}
          fallbackMaxHp={hero?.stats.hp ?? 1000}
          userId={user?.id}
          myGuildId={myGuild?.id}
          battleBracket={live?.battle_bracket ?? null}
          tournamentTree={live?.tournament_tree ?? null}
          ackRequired={!queueWatch && Boolean(live?.my_replay_ack_required)}
          onDismiss={() => {
            setSpectatorReplayDismissed(true);
            if (currentMatch) dismissReplayMatch(currentMatch.id);
          }}
          onAck={handleReplayAck}
          onWatchComplete={handleWatchComplete}
        />
      )}

      {palaceDetailKey && (
        <PalaceDetailModal
          palace={palaceDetailKey}
          onClose={() => setPalaceDetailKey(null)}
          onClaimed={() => void refresh()}
        />
      )}

      <button type="button" onClick={() => setScreen("main")} className="text-sm text-gray-400 hover:text-white">
        ← Back to Home
      </button>

      <div className="text-center space-y-1">
        <h2 className="text-xl font-bold font-game text-gold-400">🛡 Guild</h2>
        <p className="text-gray-400 text-sm">Palaces, battles, contribution shop</p>
      </div>

      {locked && (
        <div className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 text-sm text-gray-300">
          Unlocks at hero level {overview.unlock_level}. You are Lv.{hero?.level ?? 0}.
        </div>
      )}

      {error && (
        <div className="bg-rose-900/30 border border-rose-500 rounded-xl p-3 text-rose-300 text-sm">{error}</div>
      )}
      {message && (
        <div className="bg-emerald-900/30 border border-emerald-500 rounded-xl p-3 text-emerald-300 text-sm flex justify-between gap-2">
          <span>{message}</span>
          <button type="button" onClick={clearMessage} className="text-emerald-200/70">
            ✕
          </button>
        </div>
      )}

      {loading && !overview && <p className="text-center text-gray-500 text-sm py-8">Loading…</p>}

      {overview && overview.unlocked && !overview.in_guild && mode === "main" && (
        <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
          <p className="text-gray-300 text-sm">You are not in a guild yet.</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode("create")}
              className="py-3 rounded-xl bg-amber-800 text-white font-bold text-sm"
            >
              Create Guild
            </button>
            <button
              type="button"
              onClick={() => setMode("find")}
              className="py-3 rounded-xl border border-kingdom-glow text-kingdom-glow font-bold text-sm"
            >
              Find Guild
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Create costs {overview.create_cost_gold.toLocaleString()} gold
            {overview.create_cost_diamonds > 0 ? ` + ${overview.create_cost_diamonds} 💎` : ""}.
          </p>
        </section>
      )}

      {overview && overview.unlocked && !overview.in_guild && mode === "create" && (
        <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
          <h3 className="font-bold text-white">Create Guild</h3>
          <input
            value={guildName}
            onChange={(e) => setGuildName(e.target.value)}
            placeholder="Unique guild name (3–24 chars)"
            className="w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-3 py-2 text-white text-sm"
          />
          <div className="flex gap-2">
            <button type="button" onClick={() => setMode("main")} className="flex-1 py-2 rounded-lg border border-gray-600 text-gray-300 text-sm">
              Cancel
            </button>
            <button
              type="button"
              disabled={actionLoading || guildName.trim().length < 3}
              onClick={() => void createGuild(guildName.trim()).then((ok) => ok && setMode("main"))}
              className="flex-1 py-2 rounded-lg bg-amber-700 text-white font-bold text-sm disabled:opacity-40"
            >
              {actionLoading ? "Creating…" : "Create"}
            </button>
          </div>
        </section>
      )}

      {overview && overview.unlocked && !overview.in_guild && mode === "find" && (
        <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setMode("main")} className="text-gray-400 text-sm">
              ← Back
            </button>
            <h3 className="font-bold text-white">Find Guild</h3>
          </div>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name…"
            className="w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-3 py-2 text-white text-sm"
          />
          <div className="max-h-64 overflow-y-auto space-y-1">
            {searchResults.map((g) => (
              <div key={g.id} className="flex items-center justify-between gap-2 rounded-lg border border-kingdom-mid bg-kingdom-dark/60 px-3 py-2">
                <div>
                  <div className="font-bold text-white text-sm">{g.name}</div>
                  <div className="text-xs text-gray-500">
                    Lv.{g.level} · {g.member_count} members · {palaceLabel(g.palace)}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={actionLoading || g.has_pending_application}
                  onClick={() => void apply(g.id)}
                  className="text-xs px-3 py-1.5 rounded-lg bg-kingdom-mid text-kingdom-glow font-bold disabled:opacity-40"
                >
                  {g.has_pending_application ? "Pending" : "Apply"}
                </button>
              </div>
            ))}
            {searchResults.length === 0 && (
              <p className="text-xs text-gray-500 text-center py-4">No guilds found</p>
            )}
          </div>
        </section>
      )}

      {overview?.in_guild && myGuild && (
        <>
          <div className="flex gap-1 bg-kingdom-dark rounded-xl p-1 border border-kingdom-mid overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={clsx(
                  "flex-1 min-w-[4.5rem] py-2 rounded-lg text-xs font-bold",
                  tab === t.id ? "bg-kingdom-glow text-black" : "text-gray-400",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "home" && (
            <div className="space-y-3">
              <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-lg font-bold text-white">{myGuild.name}</h3>
                    <p className="text-sm text-kingdom-glow">{myGuild.role}</p>
                  </div>
                  <div className="text-right text-xs text-gray-400">
                    <div>Lv.{myGuild.level}</div>
                    <div>{myGuild.member_count} members</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <Stat label="Palace" value={palaceLabel(myGuild.palace)} />
                  <Stat label="Contribution" value={myGuild.contribution.toLocaleString()} />
                  <Stat label="Treasury" value={`${myGuild.gold_treasury.toLocaleString()}g`} />
                  <Stat label="Your gold" value={(balance?.gold ?? 0).toLocaleString()} />
                </div>
                <div className="flex gap-2 pt-1">
                  <input
                    type="number"
                    min={1}
                    value={donateAmount}
                    onChange={(e) => setDonateAmount(e.target.value)}
                    className="flex-1 rounded-lg bg-kingdom-dark border border-kingdom-mid px-2 py-1.5 text-white text-sm"
                  />
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={() => void donateGold(Number.parseInt(donateAmount, 10) || 0)}
                    className="px-3 py-1.5 rounded-lg bg-amber-800 text-white text-xs font-bold disabled:opacity-40"
                  >
                    Donate
                  </button>
                </div>
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() => void leave()}
                  className="w-full py-2 rounded-lg border border-rose-500/50 text-rose-300 text-xs font-bold"
                >
                  Leave guild
                </button>
              </section>

              <PalaceGrid palaces={overview.palaces} onSelectPalace={setPalaceDetailKey} />

              <button
                type="button"
                onClick={() => setScreen("guildTree")}
                className="w-full py-3 rounded-xl border border-emerald-600/60 bg-emerald-950/30 text-emerald-200 font-bold text-sm"
              >
                🌳 Guild Tree event
              </button>

              {overview.platinum_seats.length > 0 && (
                <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-2">
                  <h3 className="font-bold text-white text-sm">💎 Platinum Palace</h3>
                  <p className="text-xs text-gray-500">Server-wide elite seats. Tap Platinum palace for details.</p>
                  <PlatinumSeatGrid seats={overview.platinum_seats} showHolders />
                </section>
              )}

              {platinumVotes.length > 0 && (
                <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-2">
                  <h3 className="font-bold text-white text-sm">Platinum votes</h3>
                  {platinumVotes.map((v) => (
                    <div key={v.id} className="border border-kingdom-mid rounded-lg p-2 text-xs space-y-1">
                      <div className="font-bold text-white">{v.title}</div>
                      <div className="text-gray-400">{v.description}</div>
                      <div className="text-gray-500">
                        Yes {v.yes_weight} · No {v.no_weight} · {v.status}
                      </div>
                      {v.status === "open" && hasPlatinumSeat && (
                        <div className="flex gap-1 pt-1">
                          {(["yes", "no", "abstain"] as const).map((c) => (
                            <button
                              key={c}
                              type="button"
                              disabled={actionLoading}
                              onClick={() => void castPlatinumVote(v.id, c)}
                              className="px-2 py-0.5 rounded bg-kingdom-mid text-white capitalize"
                            >
                              {c}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </section>
              )}

              {isServerMaster && (
                <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-2">
                  <h3 className="font-bold text-white text-sm">Create vote (Server Master)</h3>
                  <input
                    value={voteTitle}
                    onChange={(e) => setVoteTitle(e.target.value)}
                    placeholder="Title"
                    className="w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-2 py-1.5 text-white text-xs"
                  />
                  <textarea
                    value={voteDesc}
                    onChange={(e) => setVoteDesc(e.target.value)}
                    placeholder="Description"
                    rows={2}
                    className="w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-2 py-1.5 text-white text-xs"
                  />
                  <button
                    type="button"
                    disabled={actionLoading || voteTitle.trim().length < 3}
                    onClick={() =>
                      void createPlatinumVote(voteTitle.trim(), voteDesc.trim()).then((ok) => {
                        if (ok) {
                          setVoteTitle("");
                          setVoteDesc("");
                        }
                      })
                    }
                    className="w-full py-2 rounded-lg bg-violet-800 text-white text-xs font-bold disabled:opacity-40"
                  >
                    Start vote
                  </button>
                </section>
              )}

              {myGuild.can_manage_applications && applications.length > 0 && (
                <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-2">
                  <h3 className="font-bold text-white text-sm">Pending applications ({applications.length})</h3>
                  {applications.map((app) => (
                    <div key={app.id} className="flex items-center justify-between gap-2 text-xs border border-kingdom-mid rounded-lg p-2">
                      <div>
                        <div className="text-white font-semibold">{app.display_name}</div>
                        {app.message && <div className="text-gray-500">{app.message}</div>}
                      </div>
                      <div className="flex gap-1">
                        <button type="button" onClick={() => void acceptApplication(app.id)} className="px-2 py-1 rounded bg-emerald-800 text-white">
                          ✓
                        </button>
                        <button type="button" onClick={() => void rejectApplication(app.id)} className="px-2 py-1 rounded bg-rose-900 text-white">
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </section>
              )}
            </div>
          )}

          {tab === "battle" && battle && (
            <div className="space-y-3">
              <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
                <h3 className="font-bold text-white">Guild Battle</h3>

                <GuildBattlePhaseStepper
                  phase={live?.phase ?? battle?.live?.phase}
                  active={Boolean(live?.active || live?.in_battle_window)}
                />

                <BattleScheduleCountdown battle={battle} live={live} />

                {live?.active || live?.in_battle_window ? (
                  <div className="space-y-2">
                    {!live?.active && live?.in_battle_window && (
                      <p className="text-sm text-amber-200 font-semibold animate-pulse">Opening check-in…</p>
                    )}
                    {live?.active && (
                    <>
                    <p className="text-sm text-amber-200 font-semibold">
                      {live.status === "checkin" && live.status_label
                        ? live.status_label
                        : live.status === "awaiting_replay"
                          ? live.battle_role === "spectator"
                            ? `Spectating: ${live.phase_label}`
                            : `Watch battle: ${live.phase_label}`
                          : live.status === "resolving"
                            ? live.battle_role === "spectator"
                              ? `Watching: ${live.phase_label}`
                              : `Battle in progress: ${live.phase_label}`
                            : live.status_label ?? live.phase_label}
                    </p>
                    {live.battle_role === "spectator" && live.next_battle_label && (
                      <p className="text-xs text-violet-200">{live.next_battle_label}</p>
                    )}
                    {live.status === "awaiting_replay" && live.replay_acks_required > 0 && (
                      <p className="text-[10px] text-gray-400">
                        Replays acknowledged: {live.replay_acks_received}/{live.replay_acks_required}
                      </p>
                    )}
                    {battle.battle_target_palace && live.can_enter_battle && (
                      <p className="text-xs text-gray-400">
                        Your guild target:{" "}
                        <span className="text-gold-300 capitalize">{palaceLabel(battle.battle_target_palace)}</span>
                      </p>
                    )}
                    {live.message && <p className="text-xs text-gray-500">{live.message}</p>}
                    {live.checkin_open && live.can_enter_battle && live.phase !== "platinum" && (
                      <button
                        type="button"
                        disabled={actionLoading || live.entered_battle}
                        onClick={() => void enterBattle()}
                        className={clsx(
                          "w-full py-3 rounded-xl font-bold text-sm",
                          live.entered_battle
                            ? "bg-emerald-900/40 border border-emerald-500 text-emerald-300"
                            : "bg-amber-500 text-black animate-pulse",
                        )}
                      >
                        {live.entered_battle ? "✓ Entered battle" : "Enter battle"}
                      </button>
                    )}
                    {live.checkin_open && live.battle_role === "spectator" && (
                      <p className="text-xs text-gray-400 rounded-lg border border-kingdom-mid px-3 py-2">
                        Your guild is not fighting this phase. Watch other guilds&apos; battles below — replays open
                        automatically when a match starts.
                      </p>
                    )}
                    {live.checkin_open && live.phase === "platinum" && overview && (
                      <div className="border border-violet-700 rounded-xl p-3 space-y-2">
                        <p className="text-xs text-violet-200 font-semibold">Register for a Platinum seat (5 min)</p>
                        {live.my_platinum_incumbent_seat === 0 && (
                          <p className="text-[10px] text-gray-400">
                            You hold Server Master — watch challengers; no seat selection needed.
                          </p>
                        )}
                        {(live.can_claim_platinum_seats?.length ?? 0) === 0 &&
                          live.my_platinum_incumbent_seat !== 0 &&
                          live.my_platinum_incumbent_seat != null && (
                          <p className="text-[10px] text-gray-400">
                            You hold seat #{live.my_platinum_incumbent_seat}. Contest a higher seat only (lower number).
                          </p>
                        )}
                        <PlatinumSeatGrid
                          seats={overview.platinum_seats}
                          claims={live.platinum_seat_claims}
                          myClaim={live.my_platinum_seat_claim}
                          claimMode={live.can_claim_platinum_seats.length > 0 || live.my_platinum_incumbent_seat == null}
                          allowedSeats={live.can_claim_platinum_seats}
                          actionLoading={actionLoading}
                          onClaim={(seat) => void claimPlatinumSeat(seat)}
                          showHolders={false}
                        />
                      </div>
                    )}
                    {live.last_seat_results && live.last_seat_results.length > 0 && (
                      <div className="text-xs space-y-1">
                        {live.last_seat_results.map((r) => (
                          <p key={r.seat_index} className="text-gray-400">
                            Seat #{r.seat_index}:{" "}
                            {r.result === "champion_claimed" ? (
                              <span className="text-emerald-400">claimed</span>
                            ) : (
                              <span className="text-rose-300">held by defender</span>
                            )}
                          </p>
                        ))}
                      </div>
                    )}
                    <p className="text-[10px] text-gray-500">
                      {live.checkin_count} player(s) entered
                    </p>
                    </>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">
                    Bronze → Silver → Gold → Platinum. Enter battle when check-in opens. Fighters must press Enter
                    battle — no passive online check.
                  </p>
                )}

                <GuildTournamentBracket
                  tree={live?.tournament_tree ?? null}
                  bracket={live?.battle_bracket ?? null}
                  currentMatch={live?.current_match}
                />

                <RosterEditor
                  roster={battle.roster}
                  members={members}
                  canEdit={battle.can_manage_roster}
                  actionLoading={actionLoading}
                  rosterReady={live?.roster_ready ?? []}
                  onSave={(slots) => void setRoster(slots)}
                />

                {battle.can_manage_roster && npcFillers.length > 0 && (
                  <div className="border-t border-kingdom-mid pt-3 space-y-2">
                    <div className="text-xs text-gray-400">
                      Buy NPC guards from treasury ({treasuryGold.toLocaleString()}g)
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {npcFillers.map((n) => (
                        <span key={n.tier} className="text-[10px] text-violet-300 px-2 py-1 rounded border border-violet-900">
                          T{n.tier}: {n.gold_cost.toLocaleString()}g
                        </span>
                      ))}
                    </div>
                    <p className="text-[10px] text-gray-500">
                      Guards last one battle week only — purchase again next guild war. Select an empty roster slot, then buy a tier below.
                    </p>
                  </div>
                )}
              </section>

              {battleLogs.length > 0 && (
                <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-2">
                  <h3 className="font-bold text-white text-sm">Battle log</h3>
                  {battleLogs.slice(0, 10).map((b) => (
                    <div key={b.id} className="text-xs border border-kingdom-mid rounded-lg p-2 space-y-1">
                      <div className="text-white font-semibold">
                        {b.palace.toUpperCase()} · {b.week_key}
                      </div>
                      <div className="text-gray-400">
                        {b.attacker_guild_name ?? "?"} vs {b.defender_guild_name ?? "—"} →{" "}
                        <span className="text-gold-300">{b.winner_guild_name ?? "—"}</span>
                      </div>
                      {b.skipped_reason && <div className="text-rose-300">{b.skipped_reason}</div>}
                      {b.log.length > 0 && (
                        <details className="text-gray-500">
                          <summary>{b.log.length} rounds</summary>
                          <ul className="mt-1 space-y-0.5">
                            {b.log.map((r, i) => (
                              <li key={i}>
                                R{r.round} [{r.bracket}]: {(r.team_a ?? []).join(", ")} vs {(r.team_b ?? []).join(", ")} →{" "}
                                {r.winner}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  ))}
                </section>
              )}
            </div>
          )}

          {tab === "tower" && (
            <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-white text-sm">Skill Tower</h3>
                <span className="text-xs text-kingdom-glow">
                  Contribution: {skillTower?.contribution.toLocaleString() ?? "—"}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                Upgrades persist on your character. Bonuses apply only while you are in a guild.
              </p>
              {(skillTower?.skills ?? []).map((sk) => (
                <div key={sk.code} className="border border-kingdom-mid rounded-lg p-3 space-y-1">
                  <div className="flex justify-between">
                    <span className="font-bold text-white text-sm">{sk.display_name}</span>
                    <span className="text-xs text-gray-400">
                      Lv.{sk.current_level}/{sk.max_level}
                    </span>
                  </div>
                  {sk.next_cost_contribution != null ? (
                    <button
                      type="button"
                      disabled={actionLoading || (skillTower?.contribution ?? 0) < sk.next_cost_contribution}
                      onClick={() =>
                        void guildApi.upgradeSkillTower(sk.code).then((r) => {
                          setSkillTower(r);
                          clearMessage();
                        })
                      }
                      className="text-xs px-3 py-1.5 rounded-lg bg-kingdom-mid text-white font-bold disabled:opacity-40"
                    >
                      Upgrade ({sk.next_cost_contribution.toLocaleString()} contribution)
                    </button>
                  ) : (
                    <span className="text-xs text-emerald-400">Max level</span>
                  )}
                </div>
              ))}
              {!skillTower?.enabled && <p className="text-xs text-gray-500">Skill Tower is disabled.</p>}
            </section>
          )}

          {tab === "altar" && overview?.in_guild && (
            <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4">
              <GuildAltarPanel />
            </section>
          )}

          {tab === "shop" && (
            <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-white font-bold">Guild Shop</span>
                <span className="text-kingdom-glow">Contribution: {myContribution.toLocaleString()}</span>
              </div>
              {shopItems.map((item) => (
                <div key={item.item_id} className="border border-kingdom-mid rounded-lg p-3 space-y-1">
                  <div className="font-bold text-white text-sm">{item.name}</div>
                  <div className="text-xs text-gray-400">{item.description}</div>
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-xs text-amber-300">{item.contribution_cost.toLocaleString()} contribution</span>
                    <button
                      type="button"
                      disabled={actionLoading || myContribution < item.contribution_cost}
                      onClick={() => void buyItem(item.item_id)}
                      className="text-xs px-3 py-1 rounded-lg bg-kingdom-mid text-white font-bold disabled:opacity-40"
                    >
                      Buy
                    </button>
                  </div>
                </div>
              ))}
              {shopItems.length === 0 && <p className="text-xs text-gray-500">No items available yet.</p>}
            </section>
          )}

          {tab === "members" && (
            <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-2">
              <h3 className="font-bold text-white text-sm">Members</h3>
              {members.map((m) => (
                <MemberRow
                  key={m.user_id}
                  member={m}
                  canPromote={myGuild.can_promote}
                  actionLoading={actionLoading}
                  onPromote={(role) => void promoteMember(m.user_id, role)}
                />
              ))}
              {members.length === 0 && <p className="text-xs text-gray-500">No members loaded.</p>}
            </section>
          )}
        </>
      )}

      {overview && overview.unlocked && (
        <PalaceGrid palaces={overview.palaces} compact={overview.in_guild} onSelectPalace={setPalaceDetailKey} />
      )}
    </div>
  );
}

function BattleScheduleCountdown({
  battle,
  live,
}: {
  battle: GuildBattleInfoView;
  live: GuildBattleLiveView | null;
}) {
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const scheduleText = formatGuildBattleScheduleLocal(battle.next_battle_at_utc);

  const phaseRemainingSec =
    live?.phase_ends_at != null
      ? Math.max(0, Math.floor((new Date(live.phase_ends_at).getTime() - Date.now()) / 1000))
      : live?.seconds_remaining ?? 0;

  if (live?.active && live.checkin_open && phaseRemainingSec >= 0) {
    const urgent = phaseRemainingSec < 3600;
    const withSeconds = phaseRemainingSec < 600;
    return (
      <div className="space-y-1">
        <p className="text-xs text-gray-400">{scheduleText}</p>
        <div
          className={clsx(
            "rounded-xl border px-4 py-3 text-center",
            urgent ? "border-amber-400 bg-amber-500/10" : "border-kingdom-mid bg-kingdom-dark/40",
          )}
        >
          <div className={clsx("text-[10px] uppercase tracking-wide mb-1", urgent ? "text-amber-300" : "text-gray-400")}>
            {live.phase === "platinum" ? "Seat claim ends in" : "Check-in ends in"}
          </div>
          <div className={clsx("font-game font-bold tabular-nums", urgent ? "text-3xl text-amber-300" : "text-xl text-white")}>
            {formatSecondsRemaining(phaseRemainingSec)}
          </div>
          {withSeconds && urgent && <div className="text-[10px] text-amber-400/80 mt-1">Get ready — battles start soon</div>}
        </div>
      </div>
    );
  }

  if ((live?.active && live.status === "resolving") || live?.status === "awaiting_replay") {
    return (
      <div className="space-y-1">
        <p className="text-xs text-gray-400">{scheduleText}</p>
        <div className="rounded-xl border border-rose-500/50 bg-rose-900/20 px-4 py-3 text-center text-rose-200 font-bold">
          ⚔️ {live.status === "awaiting_replay" ? "Watch battle replay" : "Battle running"} — {live.phase_label}
        </div>
      </div>
    );
  }

  if (live?.in_battle_window && !live?.active) {
    return (
      <div className="space-y-1">
        <p className="text-xs text-gray-400">{scheduleText}</p>
        <div className="rounded-xl border border-amber-400/50 bg-amber-500/10 px-4 py-3 text-center text-amber-200 font-bold animate-pulse">
          Battle window open — check-in starting…
        </div>
      </div>
    );
  }

  return (
    <p className="text-xs text-gray-400">
      {scheduleText} · next in {formatBattleCountdown(battle.next_battle_at_utc)}
    </p>
  );
}

function RosterEditor({
  roster,
  members,
  canEdit,
  actionLoading,
  rosterReady = [],
  onSave,
}: {
  roster: GuildRosterSlotView[];
  members: GuildMemberView[];
  canEdit: boolean;
  actionLoading: boolean;
  rosterReady?: RosterReadySlot[];
  onSave: (slots: { slot: number; user_id?: number | null; npc_tier?: number | null }[]) => void;
}) {
  const [local, setLocal] = useState(roster);
  const [dragUserId, setDragUserId] = useState<number | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const { buyNpc, npcFillers, canPurchaseNpc } = useGuildStore();

  const lockedSlots = useMemo(() => new Set(rosterReady.filter((r) => r.locked).map((r) => r.slot)), [rosterReady]);
  const readySlots = useMemo(() => new Set(rosterReady.filter((r) => r.ready).map((r) => r.slot)), [rosterReady]);

  useEffect(() => {
    setLocal(roster);
  }, [roster]);

  const assigned = useMemo(() => new Set(local.filter((s) => s.user_id).map((s) => s.user_id!)), [local]);

  const swapSlot = (from: number, to: number) => {
    if (lockedSlots.has(from) || lockedSlots.has(to)) return;
    setLocal((prev) => {
      const next = prev.map((s) => ({ ...s }));
      const a = next.find((s) => s.slot === from);
      const b = next.find((s) => s.slot === to);
      if (!a || !b) return prev;
      const tmpUser = a.user_id;
      const tmpNpc = a.npc_tier;
      const tmpName = a.display_name;
      const tmpClass = a.hero_class;
      const tmpNpcFlag = a.is_npc;
      a.user_id = b.user_id;
      a.npc_tier = b.npc_tier;
      a.display_name = b.display_name;
      a.hero_class = b.hero_class;
      a.is_npc = b.is_npc;
      b.user_id = tmpUser;
      b.npc_tier = tmpNpc;
      b.display_name = tmpName;
      b.hero_class = tmpClass;
      b.is_npc = tmpNpcFlag;
      return next;
    });
  };

  const assignUser = (slot: number, userId: number, displayName: string, heroClass: string | null) => {
    setLocal((prev) =>
      prev.map((s) =>
        s.slot === slot
          ? { ...s, user_id: userId, display_name: displayName, hero_class: heroClass, is_npc: false, npc_tier: null }
          : s.user_id === userId
            ? { ...s, user_id: null, display_name: null, hero_class: null, is_npc: false, npc_tier: null }
            : s,
      ),
    );
  };

  const clearSlot = (slot: number) => {
    if (lockedSlots.has(slot)) return;
    setLocal((prev) =>
      prev.map((s) =>
        s.slot === slot
          ? { ...s, user_id: null, display_name: null, hero_class: null, is_npc: false, npc_tier: null }
          : s,
      ),
    );
  };

  const toPayload = () =>
    local
      .filter((s) => s.user_id || s.npc_tier)
      .map((s) => ({
        slot: s.slot,
        user_id: s.user_id ?? null,
        npc_tier: s.npc_tier ?? null,
      }));

  if (!canEdit) {
    return (
      <div className="grid grid-cols-4 gap-1.5">
        {roster.map((slot) => (
          <div key={slot.slot} className="rounded-lg border border-kingdom-mid bg-kingdom-dark/80 p-1.5 min-h-[52px] text-[10px]">
            <div className="flex justify-between">
              <span className="text-gray-500">#{slot.slot + 1}</span>
              {readySlots.has(slot.slot) && <span className="text-emerald-400 font-bold">READY</span>}
            </div>
            <div className="text-white truncate">{slot.display_name ?? "—"}</div>
            {slot.is_npc && <div className="text-violet-300">NPC T{slot.npc_tier}</div>}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-4 gap-1.5">
        {local.map((slot) => {
          const locked = lockedSlots.has(slot.slot);
          return (
          <div
            key={slot.slot}
            draggable={!locked}
            onDragStart={() => !locked && setDragUserId(slot.user_id ?? slot.npc_tier ?? slot.slot)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => {
              if (dragUserId === null || locked) return;
              const fromSlot = local.find(
                (s) => s.user_id === dragUserId || s.npc_tier === dragUserId || s.slot === dragUserId,
              );
              if (fromSlot && fromSlot.slot !== slot.slot) swapSlot(fromSlot.slot, slot.slot);
              setDragUserId(null);
            }}
            onClick={() => setSelectedSlot(slot.slot)}
            className={clsx(
              "rounded-lg border p-1.5 min-h-[52px] text-[10px] cursor-pointer",
              selectedSlot === slot.slot ? "border-gold-400 bg-gold-900/20" : "border-kingdom-mid bg-kingdom-dark/80",
              locked && "border-emerald-600/50",
            )}
          >
            <div className="flex justify-between">
              <span className="text-gray-500">#{slot.slot + 1}</span>
              {readySlots.has(slot.slot) && <span className="text-emerald-400 font-bold">READY</span>}
            </div>
            <div className="text-white truncate">{slot.display_name ?? "—"}</div>
            {slot.is_npc && <div className="text-violet-300">NPC T{slot.npc_tier}</div>}
            {!locked && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                clearSlot(slot.slot);
              }}
              className="text-[9px] text-rose-400 mt-0.5"
            >
              clear
            </button>
            )}
          </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-1">
        {members
          .filter((m) => !assigned.has(m.user_id) || local.some((s) => s.user_id === m.user_id))
          .map((m) => (
            <button
              key={m.user_id}
              type="button"
              draggable
              onDragStart={() => setDragUserId(m.user_id)}
              disabled={assigned.has(m.user_id)}
              onClick={() => selectedSlot != null && assignUser(selectedSlot, m.user_id, m.display_name, m.hero_class)}
              className={clsx(
                "text-[10px] px-2 py-1 rounded border",
                assigned.has(m.user_id)
                  ? "border-gray-700 text-gray-600"
                  : "border-kingdom-glow text-kingdom-glow",
              )}
            >
              {m.display_name}
            </button>
          ))}
      </div>

      {selectedSlot != null && npcFillers.length > 0 && (
        <div className="space-y-1">
          {!canPurchaseNpc && (
            <p className="text-[10px] text-amber-300">Guard purchase opens during guild battle check-in.</p>
          )}
          <div className="flex flex-wrap gap-1">
            {npcFillers.map((n) => (
              <button
                key={n.tier}
                type="button"
                disabled={actionLoading || !canPurchaseNpc}
                onClick={() => void buyNpc(selectedSlot, n.tier)}
                className="text-[10px] px-2 py-1 rounded bg-violet-900 text-violet-200 disabled:opacity-40"
              >
                NPC T{n.tier} ({n.gold_cost.toLocaleString()}g)
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        disabled={actionLoading}
        onClick={() => onSave(toPayload())}
        className="w-full py-2 rounded-lg bg-kingdom-glow text-black text-xs font-bold disabled:opacity-40"
      >
        Save roster
      </button>
      <p className="text-[10px] text-gray-500">Drag slots to swap. Tap member + selected slot to assign.</p>
    </div>
  );
}

function MemberRow({
  member,
  canPromote,
  actionLoading,
  onPromote,
}: {
  member: GuildMemberView;
  canPromote: boolean;
  actionLoading: boolean;
  onPromote: (role: string) => void;
}) {
  if (member.role === "Leader") {
    return (
      <div className="flex justify-between items-center text-xs border-b border-kingdom-mid/50 py-2">
        <div>
          <div className="text-white font-semibold">{member.display_name}</div>
          <div className="text-gray-500">{member.role}</div>
        </div>
        <div className="text-gray-400 tabular-nums">{member.contribution.toLocaleString()}</div>
      </div>
    );
  }

  return (
    <div className="flex justify-between items-center text-xs border-b border-kingdom-mid/50 py-2 gap-2">
      <div className="min-w-0">
        <div className="text-white font-semibold truncate">{member.display_name}</div>
        <div className="text-gray-500">{member.role}{member.hero_class ? ` · ${member.hero_class}` : ""}</div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {canPromote && (
          <>
            <button
              type="button"
              disabled={actionLoading}
              onClick={() => onPromote("officer")}
              className="px-1.5 py-0.5 rounded bg-kingdom-mid text-[10px] text-white"
            >
              Officer
            </button>
            <button
              type="button"
              disabled={actionLoading}
              onClick={() => onPromote("deputy")}
              className="px-1.5 py-0.5 rounded bg-amber-900 text-[10px] text-white"
            >
              Deputy
            </button>
            {member.role !== "Member" && (
              <button
                type="button"
                disabled={actionLoading}
                onClick={() => onPromote("member")}
                className="px-1.5 py-0.5 rounded bg-gray-800 text-[10px] text-gray-300"
              >
                Demote
              </button>
            )}
          </>
        )}
        <span className="text-gray-400 tabular-nums w-12 text-right">{member.contribution.toLocaleString()}</span>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-kingdom-dark/60 rounded-lg px-2 py-1.5">
      <div className="text-gray-500">{label}</div>
      <div className="text-white font-semibold">{value}</div>
    </div>
  );
}

function PalaceGrid({
  palaces,
  compact,
  onSelectPalace,
}: {
  palaces: { palace: string; name: string; icon: string; holder_guild_name: string | null }[];
  compact?: boolean;
  onSelectPalace?: (palace: string) => void;
}) {
  if (compact) return null;
  return (
    <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-2">
      <h3 className="font-bold text-white text-sm">Palaces</h3>
      <div className="grid grid-cols-2 gap-2">
        {palaces.map((p) => (
          <button
            key={p.palace}
            type="button"
            onClick={() => onSelectPalace?.(p.palace)}
            className="rounded-lg border border-kingdom-mid bg-kingdom-dark/60 p-2 flex gap-2 items-center text-left hover:border-kingdom-glow transition-colors"
          >
            <img src={p.icon} alt={p.name} className="w-10 h-10 object-contain" />
            <div className="min-w-0">
              <div className="text-xs font-bold text-white truncate">{p.name}</div>
              <div className="text-[10px] text-gray-500 truncate">{p.holder_guild_name ?? "Unclaimed"}</div>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
